// The manifest's ALPHABET: the three surface verdicts a capability row is built
// from, and the four constructors that mint them. Every constructor refuses a
// blank claim AT IMPORT — see each docstring.
// Split out of `../capability-manifest.ts`, which is now the barrel over this
// directory. `yes`/`mcp`/`na`/`gap` are `export`ed only so the `rows-*` modules
// and `./reasons` can reach them, and are absent from the barrel: a caller
// outside this directory has no row to build.

/**
 * A surface this capability does NOT reach, and which of the two very different
 * reasons applies. Both carry a sentence because "absent" with no reason is the
 * prose problem again, one indirection down.
 */
export type UnservedSurface =
  /** Deliberately one-sided and expected to stay so. The question is closed. */
  | { readonly kind: 'na'; readonly reason: string }
  /** Owed and not built. The question is open. */
  | { readonly kind: 'gap'; readonly reason: string };

/**
 * REST and UI presence. Neither `served` carries an address: a REST capability is
 * addressed by the row's own key (its `REST_ROUTES` id), and the dashboard has no
 * stable address to record — the evidence for a UI claim is a call site in
 * `apps/dashboard/src`, which is where a guard for it belongs.
 */
export type SurfaceState = { readonly kind: 'served' } | UnservedSurface;

/**
 * MCP presence. `verbs` are the addresses in whichever catalog serves it — a
 * `resource.action` key of `ARMY_ACTION_SCOPES`, or a flat tool name from
 * `FLAT_TOOL_SCOPES`. They are spelled out because the two surfaces do not always
 * agree on a noun (`box.utilization` is `box.fleet_status`), so a checker cannot derive them from the id.
 *
 * A capability may carry MORE THAN ONE address when it has two MCP doors onto
 * one impl. One row, every address: a second row would duplicate the REST/UI
 * verdicts of one capability and let them drift, and leaving the second door
 * out is how the since-deleted flat `task_reassign` (#1089) shipped live but
 * uncatalogued.
 */
export type McpSurfaceState =
  | { readonly kind: 'served'; readonly verbs: readonly string[] }
  | UnservedSurface;

/** One capability's three answers. No field is optional — see the header. */
export type Capability = {
  readonly rest: SurfaceState;
  readonly mcp: McpSurfaceState;
  readonly ui: SurfaceState;
};

/** Served on REST / in the dashboard. */
export const yes: SurfaceState = { kind: 'served' };

/**
 * Served on MCP, at every `verb` given (usually one). Refuses an address-less
 * claim AT IMPORT for the same reason `na`/`gap` refuse a blank reason: a
 * `served` with nothing to check it against is unverifiable, not decided.
 */
export function mcp(...verbs: readonly string[]): McpSurfaceState {
  if (verbs.length === 0) throw new Error('CAPABILITY_MANIFEST: a served MCP row needs a verb');
  return { kind: 'served', verbs };
}

/**
 * Deliberately one-sided. Refuses a blank reason AT IMPORT — an `na` with nothing
 * behind it is worse than an absent row, because it looks decided. Same posture as
 * `REST_ROUTES`' duplicate-id throw: fail at the seam, not in a test that may not
 * run.
 */
export function na(reason: string): UnservedSurface {
  if (reason.trim() === '') throw new Error('CAPABILITY_MANIFEST: an `na` needs a reason');
  return { kind: 'na', reason };
}

/** Owed, not built. Refuses a blank reason for the same reason `na` does. */
export function gap(reason: string): UnservedSurface {
  if (reason.trim() === '') throw new Error('CAPABILITY_MANIFEST: a `gap` needs a reason');
  return { kind: 'gap', reason };
}
